import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CONCIERGE_SCOPE_LIST, MODULES, PARTNER_PATH_MODULES } from '@yourstore/domain';
import { useAction, useApi, useConsole, useRead } from './context';
import { staffAppUrlFor } from '../config';
import { Failure, OnceBox, Rich, Section, StatePill, When } from './bits';
import { ConciergeEnable } from './Concierge';
import { useT } from '../messages';
import type { T } from '../messages';
import type { IntakeMinted, JobExecution, KeyIssued } from '../wire';
import { stageName } from '../vocabulary';
import { featureName, featureTitle } from './business/features';
import { scopeSentence } from './business/connections/words';

/**
 * The acceptance path, walked once: **a new tenant from nothing to taking
 * partner bookings in under an hour, entirely through the console** (plan,
 * MVP 5).
 *
 * The wizard is a guided path over the same endpoints, not a second API. Every
 * step calls exactly the method the tenant page calls; nothing here has a
 * shortcut the ordinary screens lack, and nothing here validates something the
 * server does not. What it adds is order and the sentence between steps — which
 * is the actual difficulty of onboarding, since the steps themselves are each
 * one request.
 *
 * ## The catalog step is the interesting one, and it is a wall on purpose
 *
 * A tenant is not bookable until it has a catalog, and **this console cannot
 * create one**. That is not an omission to fill in later: `admin_api` holds no
 * grant on `items`, `resources` or anything else in a tenant's book (migration
 * 0008), because spec §6.3 says the operator console is never a data browser —
 * and that cuts both ways. A surface that must not read a tenant's catalog must
 * not write one either; "never a data browser" would be a thin promise if the
 * same seat could seed the data it is not allowed to look at.
 *
 * So that step explains, points at the two places that *can* do it — the tenant's
 * own staff app, or `apps/cli`'s `catalog seed` — and then asks the operator to
 * confirm they have done it. **It does not poll for a check that does not
 * exist.** `GET /tenants/:id` carries no service count, precisely because
 * counting a tenant's services would be reading their book; a Recheck button
 * that re-read the tenant page and looked for services would be a button that
 * cannot tell success from failure, which is worse than no button. Saying "we
 * cannot see this from here, tell us when it is done" is the honest interface
 * to a boundary we chose.
 */

/** The seven step names, in order — translated, keyed by nothing. */
function stepsOf(t: T): string[] {
  return [
    t('wizard.step.create'),
    // **Ask the owner**, and it is second on purpose: the twenty minutes an
    // owner spends on their phone is the long pole of an onboarding, so the
    // link goes out before the operator does anything else. It is also the one
    // step here that is entirely skippable — an operator may write the page by
    // hand exactly as they did before.
    t('wizard.step.intake'),
    t('wizard.step.modules'),
    t('wizard.step.key'),
    t('wizard.step.webhook'),
    t('wizard.step.catalog'),
    t('wizard.step.conformance'),
  ];
}

/**
 * **The number on each step's card, taken from the stepper.**
 *
 * The card titles used to carry their numbers in the catalog — "2 · Enable
 * modules" — and when "Ask the owner" was inserted as step 2 nothing renumbered
 * them, so the stepper said 3 while the card said 2 (console redesign, phase
 * A). The number is now the stepper's position, handed to whichever card is
 * showing, so the two can only disagree by the stepper being wrong.
 */
const StepNumber = createContext(0);

function useNumbered(): (title: string) => string {
  const n = useContext(StepNumber);
  return (title) => `${String(n)} · ${title}`;
}

export function Wizard({
  onDone,
  onCancel,
}: {
  onDone: (tenantId: string) => void;
  onCancel: () => void;
}): ReactNode {
  const t = useT();
  const [step, setStep] = useState(0);
  const [tenantId, setTenantId] = useState<string | null>(null);
  /**
   * The integration chosen at the key step, held here because the webhook step
   * after it needs the same one. The webhook step used to send `aiconcierge`
   * whatever had been typed at step 4, so a key issued for one partner got an
   * endpoint registered for another.
   */
  const [integration, setIntegration] = useState('aiconcierge');

  return (
    <div className="view">
      <header className="view__head">
        <div>
          <button type="button" className="linkish" onClick={onCancel}>
            {t('common.backToFleet')}
          </button>
          <h2>{t('wizard.title')}</h2>
        </div>
        {tenantId === null ? null : (
          <div className="view__actions">
            <button type="button" className="button" onClick={() => onDone(tenantId)}>
              {t('wizard.openTenant')}
            </button>
          </div>
        )}
      </header>

      <ol className="stepper">
        {stepsOf(t).map((label, i) => (
          <li
            key={label}
            className={`stepper__step${i === step ? ' is-current' : ''}${i < step ? ' is-done' : ''}`}
          >
            <span className="stepper__n">{i + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      <StepNumber.Provider value={step + 1}>
        {step === 0 ? (
          <CreateStep
            onCreated={(id) => {
              setTenantId(id);
              setStep(1);
            }}
          />
        ) : null}

        {step > 0 && tenantId !== null ? (
          <>
            {step === 1 ? <IntakeStep tenantId={tenantId} onNext={() => setStep(2)} /> : null}
            {step === 2 ? <ModulesStep tenantId={tenantId} onNext={() => setStep(3)} /> : null}
            {step === 3 ? (
              <KeyStep
                tenantId={tenantId}
                integration={integration}
                onIntegration={setIntegration}
                onNext={() => setStep(4)}
                // For `aiconcierge` there is no webhook to type: the server got
                // the URL back from the `PUT` and registered it. Skipping the
                // webhook step is not a shortcut, it is the step ceasing to exist.
                onSkipWebhook={() => setStep(5)}
              />
            ) : null}
            {step === 4 ? (
              <WebhookStep
                tenantId={tenantId}
                integration={integration}
                onNext={() => setStep(5)}
              />
            ) : null}
            {step === 5 ? <CatalogStep tenantId={tenantId} onNext={() => setStep(6)} /> : null}
            {step === 6 ? (
              <ConformanceStep tenantId={tenantId} onFinish={() => onDone(tenantId)} />
            ) : null}

            {step > 0 && step < 6 ? (
              <div className="row-actions">
                <button type="button" className="button" onClick={() => setStep(step - 1)}>
                  {t('common.back')}
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </StepNumber.Provider>
    </div>
  );
}

/* ---- 1. create --------------------------------------------------- */

function CreateStep({ onCreated }: { onCreated: (tenantId: string) => void }): ReactNode {
  const api = useApi();
  const t = useT();
  const numbered = useNumbered();
  const action = useAction();
  const [tenantId, setTenantId] = useState('t_');
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/St_Thomas');
  const [currency, setCurrency] = useState('USD');
  const [plan, setPlan] = useState('standard');

  const submit = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.createTenant({
        tenant_id: tenantId,
        name,
        timezone,
        currency,
        plan,
      });
      onCreated(answer.tenant_id);
    });
    if (!ok) return;
  };

  return (
    <Section title={numbered(t('wizard.create.title'))} note={t('wizard.create.note')}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="form__row">
          <label className="field">
            <span className="field__label">{t('wizard.create.tenantId')}</span>
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              aria-label={t('wizard.create.tenantId')}
            />
            <span className="field__hint">
              <Rich text={t('wizard.create.tenantIdHint')} />
            </span>
          </label>
          <label className="field field--wide">
            <span className="field__label">{t('common.name')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label={t('common.name')}
            />
          </label>
        </div>
        <div className="form__row">
          <label className="field">
            <span className="field__label">{t('wizard.create.timezone')}</span>
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              aria-label={t('wizard.create.timezone')}
            />
            <span className="field__hint">{t('wizard.create.timezoneHint')}</span>
          </label>
          <label className="field">
            <span className="field__label">{t('wizard.create.currency')}</span>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              aria-label={t('wizard.create.currency')}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('common.plan')}</span>
            <input
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              aria-label={t('common.plan')}
            />
          </label>
        </div>

        {action.failure !== null ? <Failure failure={action.failure} /> : null}

        <div className="row-actions">
          <button type="submit" className="button button--primary" disabled={action.busy}>
            {action.busy ? t('common.creating') : t('wizard.create.submit')}
          </button>
        </div>
      </form>
    </Section>
  );
}

/* ---- 2. ask the owner -------------------------------------------- */

/**
 * **Mint the owner's link and get it out of the door.**
 *
 * The only step in this wizard that produces something for somebody *outside*
 * the building, and the reason it comes second: the twenty minutes an owner
 * spends on their phone is the long pole of an onboarding, and every other step
 * here is a request an operator makes while watching. Send the link, then go do
 * the rest.
 *
 * Three things it shares with the tenant page's Intake card rather than
 * reimplementing: the API method, the `OnceBox`, and the sentence the server
 * writes about why the link cannot be shown again. What it adds is the ready-to
 * -send message, in the language the operator picked *for the owner* — which is
 * the one place on this surface where the console's own locale is not what
 * decides.
 *
 * It is **skippable and says so**. An operator who is going to write the page
 * themselves — the LoveSpa cutover, a tenant whose owner has no smartphone — is
 * not doing something unusual, and a step that could only be completed would
 * make them mint a link nobody was ever sent.
 */
function IntakeStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const api = useApi();
  const t = useT();
  const numbered = useNumbered();
  const action = useAction();
  const [template, setTemplate] = useState('one-room');
  const [locale, setLocale] = useState('en');
  const [minted, setMinted] = useState<IntakeMinted | null>(null);
  const [copied, setCopied] = useState(false);

  // The same list the branding editor's picker is built from, read off the
  // tenant's own branding payload rather than carried as a constant here.
  const branding = useRead(() => api.branding(tenantId), [tenantId]);
  const templates = branding.value?.contract.templates ?? [];

  const mint = async (): Promise<void> => {
    await action.run(async () => {
      setMinted(await api.mintIntake(tenantId, { template, locale }));
    });
  };

  const message =
    minted === null
      ? ''
      : locale.startsWith('es')
        ? t('intake.message.es', { link: minted.link })
        : t('intake.message.en', { link: minted.link });

  return (
    <Section title={numbered(t('wizard.intake.title'))} note={t('wizard.intake.note')}>
      {minted === null ? (
        <>
          <div className="form__row">
            <label className="field">
              <span className="field__label">{t('intake.template')}</span>
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                aria-label={t('intake.template')}
              >
                {templates.length === 0 ? <option value="one-room">one-room</option> : null}
                {templates.map((one) => (
                  <option key={one.name} value={one.name}>
                    {one.label} · {one.vertical}
                  </option>
                ))}
              </select>
              <span className="field__hint">{t('intake.templateHint')}</span>
            </label>
            <label className="field">
              <span className="field__label">{t('intake.language')}</span>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                aria-label={t('intake.language')}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
              <span className="field__hint">{t('intake.languageHint')}</span>
            </label>
          </div>

          {action.failure !== null ? <Failure failure={action.failure} /> : null}

          <div className="row-actions">
            <button type="button" className="button" onClick={onNext}>
              {t('wizard.intake.skip')}
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={action.busy}
              onClick={() => void mint()}
            >
              {action.busy ? t('common.working') : t('intake.mint')}
            </button>
          </div>
        </>
      ) : (
        <>
          <OnceBox
            label={t('intake.linkLabel', { host: minted.host })}
            value={minted.link}
            note={minted.link_note}
            onDismiss={onNext}
          />
          <div className="field field--wide">
            <span className="field__label">{t('intake.messageLabel')}</span>
            <textarea readOnly rows={3} value={message} aria-label={t('intake.messageLabel')} />
            <div className="row-actions">
              <button
                type="button"
                className="button"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(message)
                    .then(() => setCopied(true))
                    .catch(() => setCopied(false));
                }}
              >
                {copied ? t('once.copied') : t('intake.copyMessage')}
              </button>
            </div>
          </div>
          {minted.warnings.map((warning) => (
            <p key={warning} className="warn">
              {warning}
            </p>
          ))}
          <div className="row-actions">
            <button type="button" className="button button--primary" onClick={onNext}>
              {t('wizard.intake.continue')}
            </button>
          </div>
        </>
      )}
    </Section>
  );
}

/* ---- 3. modules -------------------------------------------------- */

function ModulesStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const api = useApi();
  const t = useT();
  const numbered = useNumbered();
  const action = useAction();
  const [picked, setPicked] = useState<string[]>([...PARTNER_PATH_MODULES]);
  const [enabled, setEnabled] = useState<string[] | null>(null);

  const submit = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setModules(tenantId, { enable: picked });
      setEnabled(answer.enabled);
    });
    if (ok) onNext();
  };

  return (
    <Section title={numbered(t('wizard.modules.title'))} note={t('wizard.modules.note')}>
      <fieldset className="scopes">
        <legend>{t('wizard.modules.legend')}</legend>
        {MODULES.filter((m) => m.kind !== 'platform').map((m) => (
          <label key={m.key} className="scope">
            <input
              type="checkbox"
              checked={picked.includes(m.key)}
              onChange={(e) =>
                setPicked((list) =>
                  e.target.checked ? [...list, m.key] : list.filter((x) => x !== m.key),
                )
              }
            />
            <span>{featureTitle(t, m.key)}</span>
            {m.dependsOn.length > 0 ? (
              <span className="muted">
                {t('wizard.modules.needs', {
                  list: m.dependsOn.map((d) => featureName(t, d)).join(', '),
                })}
              </span>
            ) : null}
          </label>
        ))}
      </fieldset>

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {enabled !== null ? (
        <p className="ok">
          {t('wizard.modules.enabled', {
            list: enabled.map((m) => featureTitle(t, m)).join(', '),
          })}
        </p>
      ) : null}

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => void submit()}
          disabled={action.busy || picked.length === 0}
        >
          {action.busy ? t('common.applying') : t('wizard.modules.submit')}
        </button>
      </div>
    </Section>
  );
}

/* ---- 4. the key -------------------------------------------------- */

/**
 * The step that changed shape.
 *
 * For **`aiconcierge`** there is no key to show and no webhook to type: one
 * server-side action mints the key, stores a signing secret, creates the tenant
 * at the partner and registers the URL the partner handed back (work item 4 of
 * the handoff; the provisioning doc §9). The `OnceBox` and the whole of step 4
 * disappear, because the credentials never reach this browser — which is the
 * entire point of doing it machine to machine.
 *
 * **Every other integration keeps today's flow**, unchanged: a key shown once,
 * then a URL and a `secret_ref` typed by a person. That is not a leftover. A
 * partner with no provisioning API of its own has no other way in, and the
 * console says which path it is on rather than hiding the difference.
 */
function KeyStep({
  tenantId,
  integration,
  onIntegration: setIntegration,
  onNext,
  onSkipWebhook,
}: {
  tenantId: string;
  integration: string;
  onIntegration: (integration: string) => void;
  onNext: () => void;
  onSkipWebhook: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const numbered = useNumbered();
  const action = useAction();
  const [issued, setIssued] = useState<KeyIssued | null>(null);

  if (integration === 'aiconcierge') {
    return (
      <Section title={numbered(t('concierge.wizard.title'))} note={t('concierge.note')}>
        <label className="field">
          <span className="field__label">{t('common.integration')}</span>
          <input
            value={integration}
            onChange={(e) => setIntegration(e.target.value)}
            aria-label={t('common.integration')}
          />
        </label>
        <ConciergeEnable tenantId={tenantId} onDone={onSkipWebhook} />
      </Section>
    );
  }

  const submit = async (): Promise<void> => {
    await action.run(async () => {
      const answer = await api.issueKey(tenantId, {
        integration,
        env: 'test',
        scopes: [...CONCIERGE_SCOPE_LIST],
      });
      setIssued(answer);
    });
  };

  return (
    <Section title={numbered(t('wizard.key.title'))} note={t('wizard.key.note')}>
      {issued === null ? (
        <>
          <label className="field">
            <span className="field__label">{t('common.integration')}</span>
            <input
              value={integration}
              onChange={(e) => setIntegration(e.target.value)}
              aria-label={t('common.integration')}
            />
          </label>
          <p className="muted">
            {/* What each scope lets the concierge do, in the "Make a key" dialog's words. */}
            <Rich
              text={t('wizard.key.scopes', {
                scopes: CONCIERGE_SCOPE_LIST.map((s) => scopeSentence(t, s).toLowerCase()).join(
                  '; ',
                ),
              })}
            />
          </p>

          {action.failure !== null ? <Failure failure={action.failure} /> : null}

          <div className="row-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => void submit()}
              disabled={action.busy}
            >
              {action.busy ? t('common.issuing') : t('keys.issue')}
            </button>
          </div>
        </>
      ) : (
        <>
          <OnceBox
            label={`${issued.prefix}… — ${issued.integration} (${issued.env})`}
            value={issued.plaintext}
            note={issued.plaintext_note}
            onDismiss={() => {
              // Dismissing drops the plaintext and advances: there is nothing
              // left to show, and a step that could show it again would be a
              // step that kept it.
              setIssued(null);
              onNext();
            }}
          />
          {issued.warnings.map((w) => (
            <p key={w} className="warn">
              {w}
            </p>
          ))}
        </>
      )}
    </Section>
  );
}

/* ---- 5. the webhook ---------------------------------------------- */

function WebhookStep({
  tenantId,
  integration,
  onNext,
}: {
  tenantId: string;
  /** The integration the key step issued for — the endpoint is registered for the same one. */
  integration: string;
  onNext: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const numbered = useNumbered();
  const action = useAction();
  const [url, setUrl] = useState('');
  const [secretRef, setSecretRef] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setWebhook(tenantId, {
        integration,
        url,
        secret_ref: secretRef,
      });
      setDone(
        answer.created
          ? t('wizard.webhook.created', { url: answer.url })
          : t('wizard.webhook.updated', { url: answer.url }),
      );
    });
    if (ok) onNext();
  };

  return (
    <Section title={numbered(t('wizard.webhook.title'))} note={t('wizard.webhook.note')}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="field field--wide">
          <span className="field__label">{t('common.url')}</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            aria-label={t('common.url')}
            placeholder="https://partner.example/hooks/yourstore"
          />
        </label>
        <label className="field field--wide">
          <span className="field__label">{t('webhooks.secretRef')}</span>
          <input
            value={secretRef}
            onChange={(e) => setSecretRef(e.target.value)}
            aria-label={t('webhooks.secretRef')}
            placeholder="projects/yourstore-dev/secrets/partner-webhook-secret/versions/latest"
          />
        </label>

        {action.failure !== null ? <Failure failure={action.failure} /> : null}
        {done !== null ? <p className="ok">{done}</p> : null}

        <div className="row-actions">
          <button type="button" className="button" onClick={onNext}>
            {t('wizard.webhook.skip')}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={action.busy || url === '' || secretRef === ''}
          >
            {action.busy ? t('common.saving') : t('wizard.webhook.submit')}
          </button>
        </div>
      </form>
    </Section>
  );
}

/* ---- 6. the catalog, which this console cannot seed --------------- */

function CatalogStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const { config } = useConsole();
  const t = useT();
  const numbered = useNumbered();
  const [confirmed, setConfirmed] = useState(false);
  const staffUrl = staffAppUrlFor(config.staffAppUrlPattern, tenantId);

  return (
    <Section title={numbered(t('wizard.catalog.title'))} note={t('wizard.catalog.note')}>
      <div className="by-design" role="note">
        <h4>{t('wizard.catalog.heading')}</h4>
        <p>{t('wizard.catalog.body')}</p>
        <p className="by-design__rule">
          <Rich text={t('wizard.catalog.rule', { tenantId })} />
        </p>
      </div>

      <p>{t('wizard.catalog.two')}</p>
      <ul className="plain-list">
        <li>
          {/*
           * The one catalog sentence split in two, because an anchor sits in the
           * middle of it: `Rich` makes `<code>` and `<strong>` out of markers,
           * and a link needs an href rather than a marker.
           */}
          <Rich text={t('wizard.catalog.owner.before')} />
          <a href={`${staffUrl}`} target="_blank" rel="noreferrer">
            {staffUrl}
          </a>
          {t('wizard.catalog.owner.after')}
        </li>
        {/*
         * The third place, added with GS4. It is listed rather than linked-to
         * because the tenant page is where this wizard ends anyway, and a
         * shortcut out of step 5 into a screen that scrolls past four other
         * cards would be a worse journey than finishing.
         */}
        <li>
          <Rich text={t('wizard.catalog.console')} />
        </li>
        <li>
          <Rich text={t('wizard.catalog.cli', { tenantId })} />
        </li>
      </ul>

      <label className="scope">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>
          <Rich text={t('wizard.catalog.confirm', { tenantId })} />
        </span>
      </label>

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={onNext}
          disabled={!confirmed}
        >
          {t('common.continue')}
        </button>
      </div>
    </Section>
  );
}

/* ---- 7. conformance ---------------------------------------------- */

/**
 * Run the suite and watch it, because "conformance green at the end of it" is
 * the acceptance sentence and an operator should see it turn green rather than
 * be told it will.
 *
 * The poll is the execution endpoint every five seconds while the state is
 * `running`, and it stops on its own — a console that kept polling a finished
 * job would be a console writing an `audit_log` row every five seconds forever.
 */
function ConformanceStep({
  tenantId,
  onFinish,
}: {
  tenantId: string;
  onFinish: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const numbered = useNumbered();
  const action = useAction();
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [execution, setExecution] = useState<JobExecution | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const tenant = useRead(() => api.tenant(tenantId), [tenantId]);

  const start = async (): Promise<void> => {
    await action.run(async () => {
      const answer = await api.runJob('conformance', { tenant_id: tenantId });
      setNote(answer.note);
      if (answer.execution !== null) {
        setExecutionId(answer.execution.execution_id);
        setExecution(answer.execution);
      }
    });
  };

  useEffect(() => {
    if (executionId === null) return;
    if (execution !== null && execution.state !== 'running' && execution.state !== 'unknown') {
      return;
    }
    let alive = true;
    const timer = setInterval(() => {
      void api
        .execution('conformance', executionId)
        .then((answer) => {
          if (alive) setExecution(answer.execution);
        })
        .catch(() => {
          // A poll that fails is not a run that failed. The last state stays on
          // screen and the next tick tries again.
        });
    }, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [api, executionId, execution]);

  const status = tenant.value?.tenant.status ?? null;

  return (
    <Section title={numbered(t('wizard.conformance.title'))} note={t('wizard.conformance.note')}>
      {status !== null && status !== 'live' ? (
        <p className="warn">
          <Rich
            text={t('wizard.conformance.notLive', { tenantId, status: stageName(t, status) })}
          />
        </p>
      ) : null}

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {/* The job service's own note about what it did or could not do. */}
      {note !== null ? <p className="muted">{note}</p> : null}

      {execution === null ? null : (
        <dl className="facts">
          <div>
            <dt>{t('jobs.th.execution')}</dt>
            <dd>
              <code>{execution.execution_id}</code>
            </dd>
          </div>
          <div>
            <dt>{t('jobs.th.state')}</dt>
            <dd>
              <StatePill state={execution.state} />
            </dd>
          </div>
          <div>
            <dt>{t('jobs.th.tasks')}</dt>
            <dd>
              {t('jobs.tasks', {
                succeeded: execution.succeeded_count ?? 0,
                total: execution.task_count ?? 0,
              })}
              {(execution.failed_count ?? 0) > 0
                ? t('wizard.conformance.failedTasks', { count: execution.failed_count ?? 0 })
                : ''}
            </dd>
          </div>
          <div>
            <dt>{t('jobs.th.completed')}</dt>
            <dd>
              <When iso={execution.completed_at} empty={t('jobs.running')} />
            </dd>
          </div>
        </dl>
      )}

      {execution?.state === 'succeeded' ? (
        <p className="ok">{t('wizard.conformance.green', { tenantId })}</p>
      ) : null}
      {execution?.state === 'failed' ? <p className="warn">{t('wizard.conformance.red')}</p> : null}

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => void start()}
          disabled={action.busy}
        >
          {action.busy
            ? t('common.starting')
            : execution === null
              ? t('jobs.run')
              : t('wizard.conformance.again')}
        </button>
        <button type="button" className="button" onClick={onFinish}>
          {t('wizard.conformance.finish')}
        </button>
      </div>
    </Section>
  );
}
